/**
 * `Intl` defaults to a maximum of 3 fraction digits, which would silently round a model value of
 * 1.23456 down to "1,235" — and since the field commits what it displays, the next blur writes
 * that rounded value back. A maximum of 20 avoids the rounding but leaks float artefacts
 * (0.1 + 0.2 renders as "0,30000000000000004"); 10 does neither.
 */
export const DEFAULT_FORMAT_OPTIONS: Intl.NumberFormatOptions = {
    useGrouping: false,
    maximumFractionDigits: 10,
};

const formatters = new Map<string, Intl.NumberFormat>();

export function getNumberFormatter(locale: string, options?: Intl.NumberFormatOptions): Intl.NumberFormat {
    const resolved = { ...DEFAULT_FORMAT_OPTIONS, ...options };
    const key = `${locale}|${JSON.stringify(resolved)}`;

    let formatter = formatters.get(key);
    if (!formatter) {
        formatter = new Intl.NumberFormat(locale, resolved);
        formatters.set(key, formatter);
    }

    return formatter;
}

/**
 * Renders a number in the given locale's notation, without grouping separators.
 *
 * @returns the formatted number, or an empty string for a null or unusable value
 */
export function formatLocalizedNumber(
    value: number | null | undefined,
    locale: string,
    options?: Intl.NumberFormatOptions,
): string {
    if (value === null || value === undefined || !Number.isFinite(value)) {
        return '';
    }

    return getNumberFormatter(locale, options).format(value);
}

export default formatLocalizedNumber;
